<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { Event, Slot } from '@/types'
import type { AttendanceParticipantDTO, AttendanceRecordDTO, SetAttendanceRequest } from '@/types'

import AttendanceGradeExternalLinkCard from '@/components/events/AttendanceGradeExternalLinkCard.vue'
import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import { useDelayedLoading } from '@/composables/useDelayedLoading'
import { getEventAttendance, setEventAttendance } from '@/services/attendanceGradeService'
import { slotService } from '@/services/slotService'
import { AttendanceStatus } from '@/types'

const props = defineProps<{
  event: Event
}>()

defineEmits<{
  (e: 'reload-event'): void
}>()

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t, d } = useI18n()
const toast = useAppToast()

const loading = ref(false)
const showLoading = useDelayedLoading(loading)
const saving = ref(false)
const records = ref<AttendanceRecordDTO[]>([])
const rosterParticipants = ref<AttendanceParticipantDTO[]>([])
const dates = ref<string[]>([])
const newDate = ref<Date | null>(null)
const hasChanges = ref(false)

// Attendance edits live in a local map until "Save", and this page registered no navigation
// guard, so leaving it dropped them silently.
const { setHasUnsavedChanges } = useUnsavedChangesProtection({
  confirmMessage: t('features.attendance.unsavedLeaveMessage'),
  confirmHeader: t('features.attendance.unsavedLeaveHeader'),
})

watch(hasChanges, (dirty) => setHasUnsavedChanges(dirty), { immediate: true })

// Slot scope selection
// 0 = entire event (sentinel), positive number = slotId
const slots = ref<Slot[]>([])
const selectedScope = ref<number>(0)
const appliedScope = ref<number>(0)

// Build a map: `${registrationId}-${appUserId}-${date}` -> AttendanceRecordDTO
const recordMap = ref<Map<string, AttendanceRecordDTO>>(new Map())
// Track pending changes
const pendingChanges = ref<Map<string, SetAttendanceRequest>>(new Map())

interface ParticipantRow {
  registrationId: number
  appUserId: string
  userName: string
}

interface ScopeOption {
  label: string
  value: number
}

const scopeOptions = computed<ScopeOption[]>(() => {
  const options: ScopeOption[] = [{ label: t('features.attendance.scopeEvent'), value: 0 }]
  for (const slot of slots.value) {
    options.push({ label: slot.name ?? `Slot #${slot.id}`, value: slot.id })
  }
  return options
})

const hasSlots = computed(() => slots.value.length > 0)

const participants = computed<ParticipantRow[]>(() => {
  return rosterParticipants.value
    .map((p) => ({
      registrationId: p.registrationId,
      appUserId: p.appUserId,
      userName: p.userName ?? t('common.terms.unknown'),
    }))
    .sort((a, b) => a.userName.localeCompare(b.userName))
})

const statusOptions = [
  {
    value: AttendanceStatus.Present,
    label: t('enums.attendanceStatus.present'),
    severity: 'success' as const,
    icon: 'i-lucide-check',
  },
  {
    value: AttendanceStatus.Absent,
    label: t('enums.attendanceStatus.absent'),
    severity: 'danger' as const,
    icon: 'i-lucide-x',
  },
  {
    value: AttendanceStatus.Excused,
    label: t('enums.attendanceStatus.excused'),
    severity: 'warn' as const,
    icon: 'i-lucide-info',
  },
]

function getRecordKey(registrationId: number, appUserId: string, date: string): string {
  return `${registrationId}-${appUserId}-${date}`
}

function getStatus(
  registrationId: number,
  appUserId: string,
  date: string,
): AttendanceStatus | null {
  const key = getRecordKey(registrationId, appUserId, date)
  const pending = pendingChanges.value.get(key)
  if (pending) return pending.clear ? null : pending.status
  const rec = recordMap.value.get(key)
  if (rec) return rec.status
  return null
}

function setStatus(
  registrationId: number,
  appUserId: string,
  date: string,
  status: AttendanceStatus,
) {
  const key = getRecordKey(registrationId, appUserId, date)
  const current = getStatus(registrationId, appUserId, date)
  const hasServerRecord = recordMap.value.has(key)

  if (current === status) {
    // Clicking the active status again — toggle off
    if (hasServerRecord) {
      pendingChanges.value.set(key, { registrationId, appUserId, date, status, clear: true })
    } else {
      pendingChanges.value.delete(key)
    }
  } else {
    pendingChanges.value.set(key, { registrationId, appUserId, date, status })
  }
  hasChanges.value = pendingChanges.value.size > 0
}

async function loadAttendance() {
  loading.value = true
  try {
    const slotId = appliedScope.value === 0 ? undefined : appliedScope.value
    const data = await getEventAttendance(props.event.id, slotId)
    records.value = data.records
    rosterParticipants.value = data.participants
    dates.value = data.dates
    // Build record map
    recordMap.value.clear()
    for (const rec of data.records) {
      const key = getRecordKey(rec.registrationId, rec.appUserId, rec.date)
      recordMap.value.set(key, rec)
    }
    pendingChanges.value.clear()
    hasChanges.value = false
  } catch (err) {
    console.error('Failed to load attendance:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.attendance.loadError'),
      duration: 5000,
    })
  } finally {
    loading.value = false
  }
}

async function saveChanges() {
  if (pendingChanges.value.size === 0) return
  saving.value = true
  try {
    await setEventAttendance(props.event.id, {
      records: Array.from(pendingChanges.value.values()),
    })
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('features.attendance.saveSuccess'),
      duration: 3000,
    })
    await loadAttendance()
  } catch (err) {
    console.error('Failed to save attendance:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.attendance.saveError'),
      duration: 5000,
    })
  } finally {
    saving.value = false
  }
}

function addDate() {
  if (!newDate.value) return
  const d = newDate.value
  const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  if (!dates.value.includes(dateStr)) {
    dates.value.push(dateStr)
    dates.value.sort()
  }
  newDate.value = null
}

function addToday() {
  const now = new Date()
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  if (!dates.value.includes(dateStr)) {
    dates.value.push(dateStr)
    dates.value.sort()
  }
}

const isTodayAdded = computed(() => {
  const now = new Date()
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return dates.value.includes(dateStr)
})

const displayDates = computed(() => [...dates.value].reverse())

function formatDateHeader(dateStr: string): string {
  return d(new Date(dateStr + 'T12:00:00'), 'dateonly')
}

interface AttendanceSummary {
  present: number
  absent: number
  excused: number
  total: number
  percentage: number
  severity: 'danger' | 'warn' | 'success'
}

function getAttendanceSummary(registrationId: number, appUserId: string): AttendanceSummary {
  let present = 0
  let absent = 0
  let excused = 0
  for (const date of dates.value) {
    const status = getStatus(registrationId, appUserId, date)
    if (status === AttendanceStatus.Present) present++
    else if (status === AttendanceStatus.Absent) absent++
    else if (status === AttendanceStatus.Excused) excused++
  }
  const total = dates.value.length
  const recorded = present + absent + excused
  const percentage = recorded > 0 ? Math.round((present / recorded) * 100) : 0
  const severity: 'danger' | 'warn' | 'success' = percentage < 80 ? 'danger' : 'success'
  return { present, absent, excused, total, percentage, severity }
}

async function loadSlots() {
  try {
    slots.value = await slotService.getAll(props.event.id)
  } catch {
    slots.value = []
  }
}

onMounted(() => {
  appliedScope.value = selectedScope.value
  loadSlots()
  loadAttendance()
})

watch(
  () => props.event.id,
  () => {
    appliedScope.value = 0
    selectedScope.value = 0
    loadSlots()
    loadAttendance()
  },
)

watch(selectedScope, async (newScope) => {
  if (newScope === appliedScope.value) {
    return
  }

  const applyScopeChange = () => {
    appliedScope.value = newScope
    loadAttendance()
  }

  if (!hasChanges.value) {
    applyScopeChange()
    return
  }

  const accepted = await appConfirm({
    message: t('features.attendance.scopeChangeConfirmMessage'),
    title: t('features.attendance.scopeChangeConfirmHeader'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.discard'),
    confirmColor: 'error',
  })
  if (accepted) applyScopeChange()
  else selectedScope.value = appliedScope.value
})
</script>

<template>
  <div>
    <div class="mb-4 flex items-center justify-between">
      <div>
        <h2 class="m-0 text-2xl font-semibold">
          {{ t('features.attendance.title') }}
        </h2>
        <p class="mt-1 mb-0 text-muted">
          {{ t('features.attendance.subtitle') }}
        </p>
      </div>
      <div class="flex gap-2">
        <UButton
          v-if="hasChanges"
          :label="t('common.actions.reset')"
          color="neutral"
          @click="loadAttendance"
          variant="outline"
        />
        <UButton
          :label="t('common.actions.save')"
          :disabled="!hasChanges"
          :loading="saving"
          @click="saveChanges"
        />
      </div>
    </div>

    <!-- Slot scope selector -->
    <div v-if="hasSlots" class="mycard mb-4 flex items-center gap-3 p-4">
      <label class="font-medium">{{ t('features.attendance.scopeLabel') }}:</label>
      <AppSelect
        v-model="selectedScope"
        :options="scopeOptions"
        option-label="label"
        option-value="value"
        class="w-72"
      />
    </div>

    <AttendanceGradeExternalLinkCard :event-id="props.event.id" :scope="appliedScope" />

    <!-- Add date row -->
    <div class="mycard mb-4 flex items-center gap-3 p-4">
      <label class="font-medium">{{ t('features.attendance.addDate') }}:</label>
      <AppDatePicker
        v-model="newDate"
        date-format="dd.mm.yy"
        :placeholder="t('features.attendance.selectDate')"
        show-icon
      />
      <UButton
        :label="t('common.actions.add')"
        icon="i-lucide-plus"
        :disabled="!newDate"
        color="neutral"
        @click="addDate"
      />
      <UButton
        :label="t('features.attendance.addToday')"
        icon="i-lucide-calendar"
        :disabled="isTodayAdded"
        color="neutral"
        @click="addToday"
        variant="outline"
      />
    </div>

    <!-- Loading -->
    <div v-if="showLoading" class="py-8 text-center">
      <AppSpinner style="width: 3rem; height: 3rem" />
    </div>

    <!-- No participants -->
    <div v-else-if="participants.length === 0" class="mycard p-8 text-center">
      <UIcon name="i-lucide-users" class="mb-2 text-4xl text-dimmed" />
      <p class="text-muted">
        {{ t('features.attendance.noParticipants') }}
      </p>
    </div>

    <!-- Attendance grid -->
    <div v-else class="mycard overflow-x-auto p-0">
      <table class="w-full border-collapse">
        <thead>
          <tr class="border-b border-neutral-200 dark:border-neutral-700">
            <th class="sticky left-0 z-10 bg-white p-3 text-left dark:bg-neutral-900">
              {{ t('features.attendance.participant') }}
            </th>
            <th v-for="date in displayDates" :key="date" class="min-w-28 p-3 text-center">
              {{ formatDateHeader(date) }}
            </th>
            <th
              v-if="dates.length > 0"
              class="sticky right-0 z-10 bg-white p-3 text-center dark:bg-neutral-900"
            >
              {{ t('features.attendance.summaryColumn') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="participant in participants"
            :key="`${participant.registrationId}-${participant.appUserId}`"
            class="border-b border-neutral-200 dark:border-neutral-700"
          >
            <td class="sticky left-0 z-10 bg-white p-3 font-medium dark:bg-neutral-900">
              {{ participant.userName }}
            </td>
            <td v-for="date in displayDates" :key="date" class="p-2 text-center">
              <div class="flex justify-center gap-1">
                <UButton
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  :icon="opt.icon"
                  :color="
                    toAppColor(
                      getStatus(participant.registrationId, participant.appUserId, date) ===
                        opt.value
                        ? opt.severity
                        : 'secondary',
                    )
                  "
                  :variant="
                    getStatus(participant.registrationId, participant.appUserId, date) !== opt.value
                      ? 'outline'
                      : 'solid'
                  "
                  size="sm"
                  v-app-tooltip.top="opt.label"
                  @click="
                    setStatus(participant.registrationId, participant.appUserId, date, opt.value)
                  "
                />
              </div>
            </td>
            <td
              v-if="dates.length > 0"
              class="sticky right-0 z-10 bg-white p-3 text-center dark:bg-neutral-900"
            >
              <template
                v-if="
                  getAttendanceSummary(participant.registrationId, participant.appUserId).total > 0
                "
              >
                <UBadge
                  :label="`${getAttendanceSummary(participant.registrationId, participant.appUserId).percentage}%`"
                  :color="
                    toAppColor(
                      getAttendanceSummary(participant.registrationId, participant.appUserId)
                        .severity,
                    )
                  "
                  v-app-tooltip.top="
                    `${t('enums.attendanceStatus.present')}: ${getAttendanceSummary(participant.registrationId, participant.appUserId).present}, ${t('enums.attendanceStatus.absent')}: ${getAttendanceSummary(participant.registrationId, participant.appUserId).absent}, ${t('enums.attendanceStatus.excused')}: ${getAttendanceSummary(participant.registrationId, participant.appUserId).excused}`
                  "
                />
              </template>
              <span v-else class="text-dimmed">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
